import React, { useMemo, useState, useEffect } from "react";
import { QuestionaryQuestion } from "../../models/QuestionaryQuestion";
import {
  Form,
  Col,
  Row,
  Table,
  Button,
  InputGroup,
  Container,
} from "react-bootstrap";
import { QuestionaryMatrixQuestion } from "../QuestionaryMatrixQuestion";
import DOMPurify from "dompurify";
import ImageDisplay from "./ImageDisplay";
import UploadFileAnswer from "./UploadFileAnswer";
import DownloadTemplate from "./DownloadTemplate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExternalLinkSquare,
  faTrash,
  faAdd,
} from "@fortawesome/free-solid-svg-icons";
import { QuestionaryAnswer } from "../../models/QuestionaryAnswer";
import QuestionaryService from "../../services/questionary.service";

interface CurrencyInputProps {
  value: string | undefined;
  onChange: (value: string) => void;
  required?: boolean;
  isInvalid?: boolean;
}

const CurrencyInput = ({
  value,
  onChange,
  required,
  isInvalid,
}: CurrencyInputProps) => {
  const [internalValue, setInternalValue] = useState(value || "");

  useEffect(() => {
    setInternalValue((prev) => {
      const prevNum = parseFloat(prev.replace(/,/g, ""));
      const valueNum = parseFloat(value || "0");

      if (prevNum !== valueNum) {
        return formatAsCurrency(value);
      }
      return prev;
    });
  }, [value]);

  const formatAsCurrency = (val: string | undefined) => {
    if (!val) return "";
    const numberValue = parseFloat(val.replace(/[^0-9.-]/g, ""));
    if (isNaN(numberValue)) return "";
    return numberValue.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;
    if (/^[0-9]*\.?[0-9]{0,2}$/.test(inputValue) || inputValue === "") {
      setInternalValue(inputValue);
    }
  };

  const handleBlur = () => {
    const cleanValue = parseFloat(internalValue.replace(/,/g, "")) || 0;
    onChange(cleanValue.toFixed(2));
    setInternalValue(formatAsCurrency(cleanValue.toFixed(2)));
  };

  return (
    <InputGroup>
      <InputGroup.Text>$</InputGroup.Text>
      <Form.Control
        type="text"
        required={required}
        value={internalValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="0.00"
        isInvalid={isInvalid}
        inputMode="decimal"
      />
    </InputGroup>
  );
};

type Props = {
  SubsectionID: number;
  questions: QuestionaryQuestion[];
  setQuestions: (value: QuestionaryQuestion[]) => void;
  invalidIds: Set<number>;
  QuestionaryId: number;
  onHandleReloadFiles: () => void;
  isNeededReloadFiles: boolean;
};

const renderComment = (comment: string | null | undefined) => {
  if (!comment) return null;
  return <Form.Text className="text-primary d-block mt-1">{comment}</Form.Text>;
};

export const DisplayQuestionaryQuestions = (props: Props) => {
  const questionsToDisplay = useMemo(
    () =>
      props.questions.filter(
        (q) => q.questionarySubsectionId === props.SubsectionID
      ),
    [props.questions, props.SubsectionID]
  );

  const updateQuestion = (
    questionId: number,
    newValues: Partial<QuestionaryQuestion>
  ) => {
    const newQuestions = props.questions.map((q) =>
      q.id === questionId ? { ...q, ...newValues } : q
    );
    props.setQuestions(newQuestions);
  };

  const handleAnswerChange = async (
    questionId: number,
    answerId: number | undefined,
    isChecked: boolean,
    questionTypeId: number
  ) => {
    const question = props.questions.find((q) => q.id === questionId);
    if (!question) return;

    const clickedAnswer = question.questionaryAnswers?.find(
      (a) => a.id === answerId
    );
    const linkedId = clickedAnswer?.linkedQuestionId;

    let questionsAfterCheckUpdate = props.questions.map((q) => {
      if (q.id === questionId) {
        const newAnswers = q.questionaryAnswers?.map((ans) => {
          if (questionTypeId === 2) {
            return { ...ans, isChecked: ans.id === answerId };
          }
          if (ans.id === answerId) {
            return { ...ans, isChecked };
          }
          return ans;
        });
        return { ...q, questionaryAnswers: newAnswers };
      }
      return q;
    });

    const previouslyCheckedAnswer = question.questionaryAnswers?.find(
      (a) => a.isChecked
    );
    if (
      questionTypeId === 2 &&
      previouslyCheckedAnswer &&
      previouslyCheckedAnswer.id !== answerId &&
      previouslyCheckedAnswer.linkedQuestionId
    ) {
      questionsAfterCheckUpdate = questionsAfterCheckUpdate.filter(
        (q) => q.id !== previouslyCheckedAnswer.linkedQuestionId
      );
    }

    if (questionTypeId === 3 && !isChecked && linkedId) {
      questionsAfterCheckUpdate = questionsAfterCheckUpdate.filter(
        (q) => q.id !== linkedId
      );
    }

    if (isChecked && linkedId) {
      const isAlreadyPresent = questionsAfterCheckUpdate.some(
        (q) => q.id === linkedId
      );
      if (!isAlreadyPresent) {
        const newQuestion = await QuestionaryService.getQuestionById(linkedId);
        const finalQuestions = [...questionsAfterCheckUpdate, newQuestion].sort(
          (a, b) => a.position - b.position
        );
        props.setQuestions(finalQuestions);
        return;
      }
    }

    props.setQuestions(
      questionsAfterCheckUpdate.sort((a, b) => a.position - b.position)
    );
  };

  if (questionsToDisplay.length === 0) {
    return <p className="text-muted">No hay preguntas en esta subsección.</p>;
  }

  return (
    <>
      {questionsToDisplay.map((question) => {
        const questionContent = () => {
          switch (question.questionTypeId) {
            case 1:
            case 5:
            case 7:
              return (
                <>
                  <Col md={5}>
                    {question.questionDesc}
                    {question.required && (
                      <span className="text-danger ms-1">*</span>
                    )}
                  </Col>
                  <Col md={7}>
                    <Form.Control
                      type={question.questionTypeId === 7 ? "date" : "text"}
                      as={question.questionTypeId === 5 ? "textarea" : "input"}
                      {...(question.questionTypeId === 5 ? { rows: 3 } : {})}
                      value={question.answerText || ""}
                      onChange={(e) =>
                        updateQuestion(question.id, {
                          answerText: e.target.value,
                        })
                      }
                      isInvalid={props.invalidIds.has(question.id)}
                      required={question.required}
                    />
                    <Form.Control.Feedback type="invalid">
                      Esta pregunta es requerida.
                    </Form.Control.Feedback>
                    {renderComment(question.questionaryAnswers?.[0]?.comment)}
                  </Col>
                </>
              );

            case 2:
            case 3:
              return (
                <>
                  <Col md={5}>
                    {question.questionDesc}
                    {question.required && (
                      <span className="text-danger ms-1">*</span>
                    )}
                  </Col>
                  <Col md={7}>
                    {question.questionaryAnswers?.map((answer) => (
                      <div key={answer.id}>
                        <Form.Check
                          type={
                            question.questionTypeId === 2 ? "radio" : "checkbox"
                          }
                          id={`q${question.id}-ans${answer.id}`}
                          name={`question-${question.id}`}
                          label={answer.answerDesc}
                          checked={answer.isChecked ?? false}
                          onChange={(e) =>
                            handleAnswerChange(
                              question.id,
                              answer.id,
                              e.target.checked,
                              question.questionTypeId
                            )
                          }
                          isInvalid={props.invalidIds.has(question.id)}
                          feedback="Debe seleccionar una opción."
                          feedbackType="invalid"
                        />
                        {renderComment(answer.comment)}
                      </div>
                    ))}
                  </Col>
                </>
              );

            case 4:
              const multiColumnAnswer = question.questionaryAnswers?.[0];
              if (!multiColumnAnswer) return null;

              const columns = JSON.parse(multiColumnAnswer.answerDesc || "[]");
              const rows = JSON.parse(question.answerText || "[]");

              const onEditMultiColumn = (
                rowIndex: number,
                colName: string,
                newValue: string
              ) => {
                const newRows = rows.map((row: any, index: number) => {
                  if (index === rowIndex) {
                    return { ...row, [`"${colName}"`]: newValue };
                  }
                  return row;
                });
                updateQuestion(question.id, {
                  answerText: JSON.stringify(newRows),
                });
              };

              const onAddMultiColumnRow = () => {
                const newRow = columns.reduce(
                  (acc: any, col: string) => ({ ...acc, [`"${col}"`]: "" }),
                  {}
                );
                const newRows = [...rows, newRow];
                updateQuestion(question.id, {
                  answerText: JSON.stringify(newRows),
                });
              };
              const onDeleteMultiColumnRow = (rowIndex: number) => {
                const newRows = rows.filter(
                  (_: any, index: number) => index !== rowIndex
                );
                updateQuestion(question.id, {
                  answerText: JSON.stringify(newRows),
                });
              };

              return (
                <Col md={12}>
                  <p>
                    {question.questionDesc}
                    {question.required && (
                      <span className="text-danger ms-1">*</span>
                    )}
                  </p>
                  <Table striped bordered hover responsive>
                    <thead className="table-secondary">
                      <tr>
                        {columns.map((col: string) => (
                          <th key={col} className="text-center">
                            {col}
                          </th>
                        ))}
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row: any, rowIndex: number) => (
                        <tr key={rowIndex}>
                          {columns.map((col: string) => (
                            <td key={col} className="text-center">
                              <Form.Control
                                type="text"
                                value={row[`"${col}"`] || ""}
                                onChange={(e) =>
                                  onEditMultiColumn(
                                    rowIndex,
                                    col,
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                          ))}
                          <td className="text-center">
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => onDeleteMultiColumnRow(rowIndex)}
                            >
                              <a>
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  style={{
                                    paddingRight: "3px",
                                  }}
                                />
                                Eliminar
                              </a>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <Button onClick={onAddMultiColumnRow}>
                    <a>
                      <FontAwesomeIcon
                        icon={faAdd}
                        style={{
                          paddingRight: "3px",
                        }}
                      />
                      Nueva fila
                    </a>
                  </Button>
                  {props.invalidIds.has(question.id) && (
                    <div className="d-block invalid-feedback">
                      Se requiere al menos una fila.
                    </div>
                  )}
                  {renderComment(multiColumnAnswer.comment)}
                </Col>
              );

            case 6:
              return (
                <Col md={12}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(question.questionDesc),
                    }}
                  />
                </Col>
              );

            case 8:
              const matrixAnswer = question.questionaryAnswers?.[0];
              if (!matrixAnswer) return null;
              return (
                <Col md={12}>
                  <p>
                    {question.questionDesc}
                    {question.required && (
                      <span className="text-danger ms-1">*</span>
                    )}
                  </p>
                  <QuestionaryMatrixQuestion
                    answer={matrixAnswer}
                    questions={props.questions}
                    setQuestions={props.setQuestions}
                  />
                  {props.invalidIds.has(question.id) && (
                    <div className="d-block invalid-feedback">
                      La matriz debe ser completada.
                    </div>
                  )}
                  {renderComment(matrixAnswer.comment)}
                </Col>
              );

            case 9:
              return (
                <Col md={12} className="text-center">
                  <p>{question.questionDesc}</p>
                  <ImageDisplay questionId={question.id} />
                </Col>
              );

            case 10:
            case 11:
              return (
                <Col md={12}>
                  <p>
                    {question.questionDesc}
                    {question.required && (
                      <span className="text-danger ms-1">*</span>
                    )}
                  </p>
                  {question.questionTypeId === 11 && (
                    <DownloadTemplate questionId={question.id} />
                  )}
                  <UploadFileAnswer
                    questionId={question.id}
                    questionaryId={props.QuestionaryId}
                    onUploadSuccess={() => console.log("upload ok")}
                    onHandleReloadFiles={props.onHandleReloadFiles}
                    isNeededReloadFiles={props.isNeededReloadFiles}
                    isInvalid={props.invalidIds.has(question.id)}
                  />
                  {renderComment(question.questionaryAnswers?.[0]?.comment)}
                </Col>
              );

            case 12:
              return (
                <>
                  <Col md={5}>
                    {question.questionDesc}
                    {question.required && (
                      <span className="text-danger ms-1">*</span>
                    )}
                  </Col>
                  <Col md={7}>
                    <CurrencyInput
                      value={question.answerText || ""}
                      onChange={(newValue) =>
                        updateQuestion(question.id, { answerText: newValue })
                      }
                      required={question.required}
                      isInvalid={props.invalidIds.has(question.id)}
                    />
                    {renderComment(question.questionaryAnswers?.[0]?.comment)}
                  </Col>
                </>
              );

            case 13:
              return (
                <Col md={12}>
                  <p>{question.questionDesc}</p>
                  <Container className="text-center">
                    <Button
                      variant="primary"
                      href={question.questionaryAnswers?.[0]?.comment || "#"}
                      target="_blank"
                    >
                      <FontAwesomeIcon
                        icon={faExternalLinkSquare}
                        className="me-2"
                      />
                      Ir al enlace
                    </Button>
                  </Container>
                </Col>
              );

            default:
              return (
                <Col>
                  <p className="text-muted">
                    Render para tipo de pregunta {question.questionTypeId} no
                    implementado.
                  </p>
                </Col>
              );
          }
        };

        return (
          <Row key={question.id} className="mb-3 align-items-center">
            {questionContent()}
          </Row>
        );
      })}
    </>
  );
};
